import {
  ChangeDetectionStrategy, Component, inject, signal, computed,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import { LocationStateService } from '../../../../../core/services/location-state.service';
import { DatePickerComponent } from '../../space-detail-panel/sections/date-picker/date-picker';
import { localDateStr } from '../../../../../core/utils/text';
import { formatClockString } from '../../../../../core/utils/locale-date';

type RepeatMode = 'none' | 'daily' | 'weekly' | 'monthly' | 'custom';

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 240];

/** Generate time slots in 15-min increments between start and end hours */
function generateTimeSlots(startHour = 7, endHour = 21): { label: string; hour: number; minute: number }[] {
  const slots: { label: string; hour: number; minute: number }[] = [];
  for (let h = startHour; h <= endHour; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === endHour && m > 0) break;
      const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      slots.push({ label, hour: h, minute: m });
    }
  }
  return slots;
}

/** Parse "HH:MM" to hour number, default to fallback */
function parseHour(time: string | undefined, fallback: number): number {
  if (!time) return fallback;
  const h = parseInt(time.split(':')[0], 10);
  return isNaN(h) ? fallback : h;
}

@Component({
  selector: 'im-date-time-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, DatePickerComponent, TranslocoPipe],
  template: `
    @if (readonly()) {
      <!-- Read-only summary: static, non-interactive -->
      <div class="dts__summary dts__summary--static" data-testid="dts-readonly">
        <paper-icon name="calendar" [size]="13" class="dts__cal-icon" />
        <div class="dts__summary-text">
          <span class="dts__summary-main">
            @if (service.startTime()) {
              {{ prettyDate() }}
              @if (service.isMultiDay() && prettyEndDate()) {
                — {{ prettyEndDate() }}
                <span class="dts__day-count">{{ daysCountLabel(dayCount()) }}</span>
              }
              · {{ clock(startTimeLabel()) }} – {{ clock(endTimeLabel()) }}
              <span class="dts__readonly-duration">({{ readonlyDurationLabel() }})</span>
            } @else {
              <span class="dts__readonly-empty">{{ 'viewer.book_meeting_panel.date_time.no_time_set' | transloco }}</span>
            }
          </span>
          @if (readonlySummaryMeta()) {
            <span class="dts__summary-meta">{{ readonlySummaryMeta() }}</span>
          }
        </div>
      </div>
    } @else {
    <!-- Collapsed summary -->
    <button class="dts__summary"
      [class.dts__summary--open]="open()"
      [attr.aria-expanded]="open()"
      (click)="open.set(!open())">
      <paper-icon name="calendar" [size]="13" class="dts__cal-icon" />
      <div class="dts__summary-text">
        <span class="dts__summary-main">
          {{ prettyDate() }}
          @if (service.isMultiDay() && prettyEndDate()) {
            — {{ prettyEndDate() }}
            <span class="dts__day-count">{{ daysCountLabel(dayCount()) }}</span>
          }
          · {{ clock(startTimeLabel()) }} — {{ clock(endTimeLabel()) }}
        </span>
        <span class="dts__summary-meta">
          {{ 'viewer.book_meeting_panel.date_time.n_min' | transloco: { count: currentDuration() } }}
          @if (service.preMinutes() > 0) {
            · {{ 'viewer.book_meeting_panel.date_time.n_setup' | transloco: { count: service.preMinutes() } }}
          }
          @if (service.postMinutes() > 0) {
            · {{ 'viewer.book_meeting_panel.date_time.n_reset' | transloco: { count: service.postMinutes() } }}
          }
          @if (repeat() !== 'none') {
            · {{ repeatLabel() }}
          }
        </span>
      </div>
      <paper-icon name="chevron-down" [size]="12" class="dts__chevron" [class.dts__chevron--open]="open()" />
    </button>

    <!-- Expanded editor -->
    @if (open()) {
      <div class="dts__editor">
        <!-- Date + Time row -->
        <div class="dts__row">
          <!-- Date picker trigger -->
          <div class="dts__field">
            <span class="dts__label">{{ 'viewer.book_meeting_panel.date_time.date' | transloco }}</span>
            <button class="dts__pill" (click)="showStartCal.set(!showStartCal())">
              <paper-icon name="calendar" [size]="11" />
              {{ prettyDate() }}
            </button>
          </div>

          @if (slotChoices().length === 0) {
            <!-- Free-form time picker (custom / interval / flexible / no slot config) -->
            <div class="dts__field">
              <span class="dts__label">{{ 'viewer.book_meeting_panel.date_time.start' | transloco }}</span>
              <button class="dts__pill" (click)="showStartTime.set(!showStartTime())">
                <paper-icon name="clock" [size]="11" />
                {{ clock(startTimeLabel()) }}
              </button>
            </div>

            <div class="dts__field">
              <span class="dts__label">{{ 'viewer.book_meeting_panel.date_time.duration' | transloco }}</span>
              <select class="dts__select"
                [value]="currentDuration()"
                (change)="onDurationChange($any($event.target).value)">
                @for (d of durations; track d) {
                  <option [value]="d">{{ 'viewer.book_meeting_panel.date_time.n_min' | transloco: { count: d } }}</option>
                }
              </select>
            </div>
          }
        </div>

        @if (slotChoices().length > 0) {
          <!-- Fixed slot chooser (single / ampm) — admin-authored windows -->
          <div class="dts__slot-row">
            <span class="dts__label">{{ 'viewer.book_meeting_panel.date_time.slot' | transloco }}</span>
            <div class="dts__slot-chips">
              @for (slot of slotChoices(); track slot.label) {
                <button class="dts__slot-chip"
                  [class.active]="isSlotActive(slot)"
                  (click)="onSlotPicked(slot)">
                  <span class="dts__slot-chip-name">{{ slot.label }}</span>
                  <span class="dts__slot-chip-window">{{ clock(slot.startTime) }} – {{ clock(slot.endTime) }}</span>
                </button>
              }
            </div>
          </div>
        }

        <!-- Inline calendar for start date -->
        @if (showStartCal()) {
          <div class="dts__calendar-wrap">
            <dp-date-picker
              [selected]="selectedDateStr()"
              (dateSelected)="onDatePicked($event)" />
          </div>
        }

        <!-- Start time dropdown -->
        @if (showStartTime()) {
          <div class="dts__time-dropdown">
            @for (slot of timeSlots(); track slot.label) {
              <button class="dts__time-option"
                [class.active]="slot.label === startTimeLabel()"
                (click)="onStartTimePicked(slot)">
                {{ clock(slot.label) }}
              </button>
            }
          </div>
        }

        <!-- Multi-day toggle -->
        <div class="dts__multi-day-row">
          <paper-icon name="calendar-range" [size]="12" />
          <span class="dts__multi-label">{{ 'viewer.book_meeting_panel.date_time.multi_day' | transloco }}</span>
          <button class="dts__toggle" role="switch"
            [attr.aria-checked]="service.isMultiDay()"
            (click)="service.isMultiDay.set(!service.isMultiDay())">
            <span class="dts__toggle-thumb" [class.on]="service.isMultiDay()"></span>
          </button>
        </div>

        <!-- End date (multi-day) -->
        @if (service.isMultiDay()) {
          <div class="dts__row" style="margin-top: 8px">
            <div class="dts__field">
              <span class="dts__label">{{ 'viewer.book_meeting_panel.date_time.end_date' | transloco }}</span>
              <button class="dts__pill" (click)="showEndCal.set(!showEndCal())">
                <paper-icon name="calendar" [size]="11" />
                {{ prettyEndDate() || selectLabel() }}
              </button>
            </div>
            @if (dayCount() > 0) {
              <div class="dts__field">
                <span class="dts__label">&nbsp;</span>
                <span class="dts__day-badge">{{ daysCountLabel(dayCount()) }}</span>
              </div>
            }
          </div>

          @if (showEndCal()) {
            <div class="dts__calendar-wrap">
              <dp-date-picker
                [selected]="endDateStr()"
                (dateSelected)="onEndDatePicked($event)" />
            </div>
          }
        }

        <!-- Recurrence (hidden when multi-day OR policy disallows) -->
        @if (!service.isMultiDay() && service.recurrenceAllowed()) {
          <div class="dts__repeat-section">
            <span class="dts__label">{{ 'viewer.book_meeting_panel.date_time.repeat' | transloco }}</span>
            <div class="dts__repeat-chips">
              @for (mode of repeatModes(); track mode.value) {
                <button class="dts__repeat-chip"
                  [class.active]="repeat() === mode.value"
                  (click)="repeat.set(mode.value)">
                  {{ mode.label }}
                </button>
              }
            </div>
            @if (repeat() !== 'none') {
              <div class="dts__repeat-stepper">
                <span class="dts__repeat-stepper-label">{{ 'viewer.book_meeting_panel.date_time.repeat' | transloco }}</span>
                <div class="dts__stepper">
                  <button class="dts__stepper-btn" (click)="decrementRepeat()">−</button>
                  <span class="dts__stepper-val">{{ repeatCount() }}</span>
                  <button class="dts__stepper-btn" (click)="incrementRepeat()">+</button>
                </div>
                <span class="dts__repeat-stepper-label">{{ timesLabel(repeatCount()) }}</span>
              </div>
              <!-- Policy limits inline (T4.7) — only when the backend
                   sends them; legacy responses render nothing here. -->
              @if (service.maxRecurringOccurrences() !== null) {
                <div class="dts__repeat-limit">
                  {{ 'viewer.book_meeting_panel.date_time.repeat_limit' | transloco:{ count: service.maxRecurringOccurrences() } }}
                  @if (service.maxRecurringDaysAhead() !== null) {
                    · {{ 'viewer.book_meeting_panel.date_time.repeat_limit_days' | transloco:{ count: service.maxRecurringDaysAhead() } }}
                  }
                </div>
              }
            }
          </div>
        }

        <!-- Hint -->
        <div class="dts__hint">
          <paper-icon name="sparkles" [size]="10" class="dts__hint-icon" />
          {{ 'viewer.book_meeting_panel.date_time.pulled_from_schedule' | transloco }}
        </div>
      </div>
    }
    }
  `,
  styles: [`
    :host { display: block; margin-top: 12px; }

    /* ── Summary row ──────────────────────── */
    /* Mirrors the prototype's .bp-when-pill — a single raised pill
       sitting on the panel canvas. Uses --surface-raised (white on
       Workplace Paper) so it reads brighter than the cream canvas
       behind it, with a thin divider border. */
    .dts__summary {
      width: 100%; padding: 10px 12px;
      background: var(--surface-raised, var(--surface-1, var(--hover-overlay)));
      border: 1px solid var(--divider);
      border-radius: 10px; text-align: left;
      display: flex; align-items: center; gap: 10px;
      cursor: pointer; font: inherit; color: var(--text);
      transition: border-color 0.15s;

      &:hover { border-color: var(--border-strong, var(--text-secondary)); }
    }

    /* Read-only static summary — no hover, no chevron, no click */
    .dts__summary--static {
      cursor: default;
      &:hover { border-color: var(--divider); }
    }

    .dts__readonly-empty {
      color: var(--text-tertiary);
      font-style: italic;
    }

    .dts__readonly-duration {
      color: var(--text-tertiary);
      font-weight: 400;
      margin-left: 4px;
    }

    .dts__cal-icon { color: var(--bmp-accent-text); flex: none; }

    .dts__summary-text { flex: 1; min-width: 0; }

    .dts__summary-main {
      font-size: 12.5px; font-variant-numeric: tabular-nums;
      display: block;
    }

    .dts__summary-meta {
      font-size: 10.5px; color: var(--text-tertiary); margin-top: 1px;
      display: flex; align-items: center; gap: 6px;
    }

    .dts__day-count {
      font-size: 10px; font-weight: 600;
      color: var(--bmp-accent-text);
      margin-left: 4px;
    }

    .dts__chevron {
      color: var(--text-tertiary); flex: none;
      transition: transform 0.2s;
      &--open { transform: rotate(180deg); }
    }

    /* ── Editor ───────────────────────────── */
    .dts__editor {
      margin-top: 6px; padding: 12px;
      background: var(--hover-overlay);
      border: 1px solid var(--divider);
      border-radius: 10px;
    }

    .dts__row {
      display: flex; gap: 8px; align-items: flex-start;
    }

    .dts__field {
      display: flex; flex-direction: column; gap: 3px; flex: 1;
    }

    .dts__label {
      font-size: 10px; font-weight: 600; color: var(--text-tertiary);
      letter-spacing: 0.06em; text-transform: uppercase;
    }

    /* ── Pill buttons (date/time triggers) ─── */
    /* Cream-tone fill matching the prototype's .bp-when-pill — sits
       softer in the panel than the bold-white the pill used before. */
    .dts__pill {
      padding: 7px 10px; font-size: 12px;
      background: var(--surface-sunken, var(--focus-overlay));
      border: 1px solid var(--glass-border);
      border-radius: 7px; color: var(--text);
      cursor: pointer; font: inherit; font-size: 12px;
      font-variant-numeric: tabular-nums;
      display: inline-flex; align-items: center; gap: 6px;
      transition: border-color 0.15s;

      &:hover { border-color: var(--glass-border-hover); }
    }

    /* ── Duration select ──────────────────── */
    .dts__select {
      padding: 7px 26px 7px 9px; font-size: 12px;
      background: var(--surface-sunken, var(--focus-overlay));
      border: 1px solid var(--glass-border);
      border-radius: 7px; color: var(--text);
      font: inherit; font-size: 12px;
      font-variant-numeric: tabular-nums;
      outline: none; cursor: pointer;
      appearance: none; -webkit-appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 16 16' fill='none' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 8px center;
    }

    /* ── Calendar ──────────────────────────── */
    .dts__calendar-wrap {
      margin-top: 8px;
      background: var(--surface-1);
      border: 1px solid var(--border-med);
      border-radius: 10px;
      overflow: hidden;
    }

    /* ── Time dropdown ─────────────────────── */
    .dts__time-dropdown {
      margin-top: 8px;
      background: var(--surface-1);
      border: 1px solid var(--border-med);
      border-radius: 10px;
      max-height: 200px; overflow-y: auto;
      display: grid; grid-template-columns: repeat(4, 1fr);
      gap: 2px; padding: 4px;
    }

    .dts__time-option {
      padding: 6px 4px; font-size: 11px;
      font-variant-numeric: tabular-nums;
      border-radius: 6px; text-align: center;
      background: none; border: none; font: inherit;
      font-size: 11px; color: var(--text-secondary);
      cursor: pointer;

      &:hover { background: var(--hover-overlay); color: var(--text); }
      &.active {
        background: var(--bmp-accent-soft, rgba(16,185,129,0.12));
        color: var(--bmp-accent-text);
        font-weight: 600;
      }
    }

    /* ── Multi-day toggle ─────────────────── */
    .dts__multi-day-row {
      display: flex; align-items: center; gap: 8px;
      margin-top: 12px; padding-top: 10px;
      border-top: 1px solid var(--divider);
      color: var(--text-secondary); font-size: 12px;
    }

    .dts__multi-label { flex: 1; font-weight: 500; }

    .dts__toggle {
      width: 34px; height: 20px; border-radius: 100px;
      background: var(--glass-border);
      border: 1px solid var(--glass-border);
      position: relative; flex: none; cursor: pointer;
      transition: background 0.2s;

      &[aria-checked="true"] { background: var(--bmp-accent); border-color: var(--bmp-accent); }
    }

    .dts__toggle-thumb {
      position: absolute; top: 1px; left: 2px;
      width: 16px; height: 16px; border-radius: 50%;
      background: var(--text-secondary);
      transition: left 0.2s cubic-bezier(0.16, 1, 0.3, 1);

      &.on { left: 14px; background: var(--surface-1); }
    }

    .dts__day-badge {
      padding: 6px 12px; font-size: 12px; font-weight: 600;
      color: var(--bmp-accent-text);
      background: var(--bmp-accent-soft, rgba(16,185,129,0.12));
      border-radius: 7px;
    }

    /* ── Repeat ────────────────────────────── */
    .dts__repeat-section {
      margin-top: 10px; padding-top: 10px;
      border-top: 1px solid var(--divider);
    }

    .dts__repeat-chips {
      display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px;
    }

    .dts__repeat-chip {
      padding: 4px 10px; font-size: 11px; font-weight: 500;
      border-radius: 100px; cursor: pointer;
      font: inherit; font-size: 11px; font-weight: 500;
      background: transparent;
      color: var(--text-secondary);
      border: 1px solid var(--glass-border);
      transition: all 0.15s;

      &.active {
        background: var(--bmp-accent-soft, rgba(16,185,129,0.12));
        color: var(--bmp-accent-text);
        border-color: rgba(16, 185, 129, 0.25);
      }
    }

    .dts__repeat-stepper {
      display: flex; align-items: center; gap: 10px; margin-top: 10px;
    }

    .dts__repeat-stepper-label {
      font-size: 11px; color: var(--text-secondary);
    }

    // Inline policy limits — "Up to N occurrences · within M days" (T4.7)
    .dts__repeat-limit {
      font-size: 10px; color: var(--text-tertiary);
      margin-top: 4px;
    }

    .dts__stepper {
      display: inline-flex; align-items: center;
      background: var(--focus-overlay);
      border: 1px solid var(--glass-border);
      border-radius: 8px;
    }

    .dts__stepper-btn {
      width: 26px; height: 26px; font-size: 14px;
      color: var(--text-secondary);
      background: none; border: none; cursor: pointer;
      font: inherit; font-size: 14px;
    }

    .dts__stepper-val {
      padding: 0 10px; font-size: 12px; min-width: 36px;
      text-align: center; font-variant-numeric: tabular-nums;
    }

    /* ── Fixed slot chooser (single / ampm) ───── */
    .dts__slot-row {
      margin-top: 12px;
      display: flex; flex-direction: column; gap: 6px;
    }

    .dts__slot-chips {
      display: flex; flex-wrap: wrap; gap: 6px;
    }

    .dts__slot-chip {
      padding: 8px 12px; font: inherit; cursor: pointer;
      background: var(--focus-overlay);
      border: 1px solid var(--glass-border);
      border-radius: 8px; color: var(--text);
      display: inline-flex; flex-direction: column; align-items: flex-start; gap: 2px;
      transition: border-color 0.15s, background 0.15s;

      &:hover { border-color: var(--glass-border-hover); }
      &.active {
        border-color: var(--bmp-accent);
        background: var(--bmp-accent-soft, rgba(16,185,129,0.12));
        color: var(--bmp-accent-text);
        font-weight: 600;
      }
    }

    .dts__slot-chip-name { font-size: 12px; }
    .dts__slot-chip-window {
      font-size: 10.5px; color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
    }

    /* ── Hint ──────────────────────────────── */
    .dts__hint {
      margin-top: 10px;
      display: flex; align-items: center; gap: 6px;
      font-size: 10.5px; color: var(--text-tertiary);
    }

    .dts__hint-icon { color: var(--bmp-hint-icon); }

    @media (prefers-reduced-motion: reduce) {
      .dts__summary, .dts__pill, .dts__repeat-chip,
      .dts__toggle, .dts__toggle-thumb, .dts__chevron { transition: none; }
    }
  `],
})
export class DateTimeSectionComponent {
  protected readonly service = inject(BookMeetingService);
  private readonly locationState = inject(LocationStateService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected selectLabel(): string {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.date_time.select');
  }

  protected daysCountLabel(count: number): string {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.date_time.n_days', { count });
  }

  protected timesLabel(count: number): string {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.date_time.n_times', { count });
  }

  /** Driven by BookMeetingService.mode — when 'view', the section renders
   *  a static summary instead of the editor. */
  protected readonly readonly = computed(() => this.service.mode() === 'view');

  // ── Local UI state ─────────────────────────────
  protected readonly open = signal(false);

  /** Recurrence signals — delegated to service for submission */
  protected readonly repeat = this.service.recurrenceMode;
  protected readonly repeatCount = this.service.recurrenceCount;
  protected readonly showStartCal = signal(false);
  protected readonly showEndCal = signal(false);
  protected readonly showStartTime = signal(false);

  protected readonly durations = DURATION_OPTIONS;

  /** Time slots derived from office hours (or 07:00-21:00 fallback) */
  protected readonly timeSlots = computed(() => {
    const oh = this.locationState.officeHours();
    const startH = parseHour(oh?.start, 7);
    const endH = parseHour(oh?.end, 21);
    return generateTimeSlots(Math.max(0, startH - 1), Math.min(23, endH + 2));
  });

  protected readonly repeatModes = computed<{ value: RepeatMode; label: string }[]>(() => {
    this.locale.locale();
    const t = (k: string) => this.transloco.translate(k);
    return [
      { value: 'none', label: t('viewer.book_meeting_panel.date_time.repeat_none') },
      { value: 'daily', label: t('viewer.book_meeting_panel.date_time.repeat_daily') },
      { value: 'weekly', label: t('viewer.book_meeting_panel.date_time.repeat_weekly') },
      { value: 'monthly', label: t('viewer.book_meeting_panel.date_time.repeat_monthly') },
      { value: 'custom', label: t('viewer.book_meeting_panel.date_time.repeat_custom') },
    ];
  });

  // ── Computed display values ────────────────────

  protected readonly prettyDate = computed(() => {
    this.locale.locale();
    const start = this.service.startTime();
    if (!start) return this.transloco.translate('viewer.book_meeting_panel.date_time.select_date');
    return start.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  });

  protected readonly prettyEndDate = computed(() => {
    const end = this.service.endDate();
    if (!end) return null;
    return end.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  });

  /** DISPLAY only — the "HH:mm" labels below are also compared as keys
   *  (`isSlotActive`, the active option), so they stay machine strings and
   *  take the viewer's clock only where they are printed. */
  protected clock(hhmm: string): string {
    return formatClockString(hhmm, this.locale.dateFnsLocale());
  }

  protected readonly startTimeLabel = computed(() => {
    const start = this.service.startTime();
    if (!start) return '--:--';
    return `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
  });

  protected readonly endTimeLabel = computed(() => {
    const end = this.service.endTime();
    if (!end) return '--:--';
    return `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
  });

  protected readonly currentDuration = computed(() => {
    const dur = this.service.duration();
    return dur ?? 30;
  });

  protected readonly selectedDateStr = computed(() => {
    const start = this.service.startTime();
    return start ? localDateStr(start) : localDateStr(new Date());
  });

  protected readonly endDateStr = computed(() => {
    const end = this.service.endDate();
    return end ? localDateStr(end) : this.selectedDateStr();
  });

  protected readonly dayCount = computed(() => {
    const days = this.service.meetingDays();
    return days.length;
  });

  protected readonly repeatLabel = computed(() => {
    this.locale.locale();
    const r = this.repeat();
    const c = this.repeatCount();
    if (r === 'none') return '';
    const label = this.transloco.translate(`viewer.book_meeting_panel.date_time.repeat_${r}`);
    return `${label} × ${c}`;
  });

  /** Human-friendly duration label for the read-only summary. Falls back
   *  to "—" when no times are set. */
  protected readonly readonlyDurationLabel = computed(() => {
    const dur = this.service.duration();
    if (dur == null) return '—';
    if (dur < 60) return `${dur}m`;
    const hours = Math.floor(dur / 60);
    const mins = dur % 60;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  });

  /** Compose the "setup / reset / recurrence / occurrences" line below the
   *  primary date+time summary. Returns empty string when there's nothing
   *  to surface so the template can skip rendering entirely. */
  protected readonly readonlySummaryMeta = computed(() => {
    this.locale.locale();
    const parts: string[] = [];
    if (this.service.preMinutes() > 0) parts.push(this.transloco.translate('viewer.book_meeting_panel.date_time.n_setup', { count: this.service.preMinutes() }));
    if (this.service.postMinutes() > 0) parts.push(this.transloco.translate('viewer.book_meeting_panel.date_time.n_reset', { count: this.service.postMinutes() }));
    const r = this.repeat();
    if (r !== 'none') {
      const label = this.transloco.translate(`viewer.book_meeting_panel.date_time.repeat_${r}`);
      parts.push(this.transloco.translate('viewer.book_meeting_panel.date_time.recurs_n_occurrences', { recurs: label.toLowerCase(), count: this.repeatCount() }));
    }
    return parts.join(' · ');
  });

  /** Fixed slot windows authored on the space — only present for slotConfig
   *  modes that constrain the booking to a finite list of choices (single,
   *  ampm). Empty list ⇒ free-form picker stays in place. */
  protected readonly slotChoices = computed(() => {
    const cfg = this.service.primarySlotConfig();
    if (!cfg) return [];
    if (cfg.mode !== 'single' && cfg.mode !== 'ampm') return [];
    const slots = (cfg.bookingSlots ?? []).filter(
      (s): s is { startTime: string; endTime: string; name?: string } =>
        !!s.startTime && !!s.endTime,
    );
    return slots.map((s, i) => ({
      label: s.name?.trim() || this.transloco.translate('viewer.book_meeting_panel.date_time.slot_n', { number: i + 1 }),
      startTime: s.startTime,
      endTime: s.endTime,
    }));
  });

  // ── Event handlers ─────────────────────────────

  protected onDatePicked(dateStr: string): void {
    const start = this.service.startTime();
    const [y, m, d] = dateStr.split('-').map(Number);
    const newStart = new Date(start || new Date());
    newStart.setFullYear(y, m - 1, d);
    this.service.startTime.set(newStart);

    // Update end time to keep same duration
    const dur = this.currentDuration();
    this.service.endTime.set(new Date(newStart.getTime() + dur * 60000));

    this.showStartCal.set(false);
  }

  protected onEndDatePicked(dateStr: string): void {
    const [y, m, d] = dateStr.split('-').map(Number);
    const endDate = new Date(y, m - 1, d, 12, 0, 0);
    this.service.endDate.set(endDate);
    this.showEndCal.set(false);
  }

  protected onStartTimePicked(slot: { label: string; hour: number; minute: number }): void {
    const start = this.service.startTime() || new Date();
    const newStart = new Date(start);
    newStart.setHours(slot.hour, slot.minute, 0, 0);
    this.service.startTime.set(newStart);

    const dur = this.currentDuration();
    this.service.endTime.set(new Date(newStart.getTime() + dur * 60000));
    this.showStartTime.set(false);
  }

  /** Apply a fixed slot — writes both start and end so the duration matches
   *  the admin-authored window exactly. The picker prevents the user from
   *  building an invalid request that the backend would reject. */
  protected onSlotPicked(slot: { startTime: string; endTime: string }): void {
    const base = this.service.startTime() || new Date();
    const [sh, sm] = slot.startTime.split(':').map(Number);
    const [eh, em] = slot.endTime.split(':').map(Number);
    const newStart = new Date(base);
    newStart.setHours(sh, sm, 0, 0);
    const newEnd = new Date(base);
    newEnd.setHours(eh, em, 0, 0);
    this.service.startTime.set(newStart);
    this.service.endTime.set(newEnd);
  }

  protected isSlotActive(slot: { startTime: string; endTime: string }): boolean {
    return (
      this.startTimeLabel() === slot.startTime &&
      this.endTimeLabel() === slot.endTime
    );
  }

  protected decrementRepeat(): void {
    this.repeatCount.update(c => c > 1 ? c - 1 : 1);
  }

  /**
   * Increment caps at the policy ceiling (maxRecurringOccurrences, legacy
   * hard cap 52 when the backend sends none — T4.7). A blocked attempt
   * fires the typed clamp event so policy friction is measurable.
   */
  protected incrementRepeat(): void {
    const ceiling = this.service.recurrenceCountCeiling();
    const current = this.repeatCount();
    if (current >= ceiling) {
      this.service.captureRecurrenceClamp('stepper', current + 1, ceiling);
      this.repeatCount.set(ceiling);
      return;
    }
    this.repeatCount.set(current + 1);
  }

  protected onDurationChange(value: string): void {
    const dur = parseInt(value, 10);
    const start = this.service.startTime();
    if (start) {
      this.service.endTime.set(new Date(start.getTime() + dur * 60000));
    }
  }
}
